import React, { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Settings, X, Shield, BarChart3, Megaphone } from "lucide-react";
import {
  getStoredConsent,
  acceptAll,
  rejectAll,
  saveConsent,
  isQuebecVisitor,
  CONSENT_CATEGORIES,
  CONSENT_VERSION,
} from "../services/cookieConsentService";

/**
 * Cookie Consent Banner — North American defaults
 *
 * Two UX modes:
 *
 * 1. Non-Quebec visitors (US/MX/non-QC Canada): informational notice
 *    that can be dismissed with "Got it". Analytics is already granted
 *    by default (implied consent — legal in these jurisdictions).
 *    Users who want to opt out can click "Manage cookies" or use the
 *    footer link at any time. Marketing remains opt-in.
 *
 * 2. Quebec visitors (Loi 25): traditional opt-in banner with
 *    "Accept All" / "Reject All" / "Customize" — explicit consent
 *    required before analytics or marketing fires.
 *
 * Detection is approximate (language-based via isQuebecVisitor() in
 * cookieConsentService.js). En-CA users physically in QC slip through
 * but >80% of QC residents browse with fr-CA and are caught.
 */
function CookieBanner() {
  const location = useLocation();
  const { t } = useTranslation();
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  // Quebec visitors get the strict opt-in flow; everyone else gets the
  // informational notice with implied consent for analytics.
  const inQuebec = isQuebecVisitor();

  const [preferences, setPreferences] = useState({
    necessary: true,            // Always true, can't be disabled
    analytics: !inQuebec,       // Granted by default for non-QC (implied)
    marketing: false,           // Always opt-in (denied by default)
  });

  // Check if running in native app (Capacitor)
  const isNativeApp =
    window.Capacitor?.isNativePlatform?.() ||
    document.body.classList.contains("capacitor-android") ||
    document.body.classList.contains("capacitor-ios") ||
    window.isNativeApp === true;

  // Handle opening settings from external trigger (e.g., footer link)
  const handleOpenSettings = useCallback(() => {
    const existingConsent = getStoredConsent();
    if (existingConsent) {
      setPreferences({
        necessary: true,
        analytics: existingConsent.analytics || false,
        marketing: existingConsent.marketing || false,
      });
    }
    setShowSettings(true);
    setShowBanner(true);
  }, []);

  useEffect(() => {
    // Don't show cookie banner in native apps
    if (isNativeApp) {
      return;
    }

    // Check if user has already made a consent decision
    const existingConsent = getStoredConsent();
    if (!existingConsent) {
      setShowBanner(true);
    } else {
      // Pre-fill preferences with existing consent
      setPreferences({
        necessary: true,
        analytics: existingConsent.analytics || false,
        marketing: existingConsent.marketing || false,
      });
    }

    // Listen for external requests to open cookie settings
    window.addEventListener("openCookieSettings", handleOpenSettings);

    return () => {
      window.removeEventListener("openCookieSettings", handleOpenSettings);
    };
  }, [isNativeApp, handleOpenSettings]);

  // Don't show cookie banner on admin pages, native app pages, or in native apps
  if (
    isNativeApp ||
    location.pathname.startsWith("/admin") ||
    location.pathname.startsWith("/native-") ||
    location.pathname.startsWith("/mileage-tracker") ||
    location.pathname.startsWith("/mileage-history") ||
    location.pathname.startsWith("/mileage-settings") ||
    location.pathname.startsWith("/vehicle-expenses")
  ) {
    return null;
  }

  const handleAcceptAll = () => {
    acceptAll("banner");
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleRejectAll = () => {
    rejectAll("banner");
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleSavePreferences = () => {
    saveConsent({
      analytics: preferences.analytics,
      marketing: preferences.marketing,
      method: "settings",
    });
    setShowBanner(false);
    setShowSettings(false);
  };

  // Non-QC implicit-consent dismiss: persists the default state
  // (analytics granted, marketing denied) so the banner doesn't re-appear
  // and GTM Consent Mode receives a definitive signal.
  const handleAcknowledge = () => {
    saveConsent({
      analytics: true,
      marketing: false,
      method: "banner_dismiss",
    });
    setShowBanner(false);
    setShowSettings(false);
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Settings Modal */}
      {showSettings && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10001,
            padding: "20px",
          }}
          onClick={() => setShowSettings(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="cookie-settings-title"
        >
          <div
            style={{
              background: "#fff",
              borderRadius: "12px",
              maxWidth: "500px",
              width: "100%",
              maxHeight: "80vh",
              overflow: "auto",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div
              style={{
                padding: "20px 24px",
                borderBottom: "1px solid #e5e7eb",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <h2
                id="cookie-settings-title"
                style={{
                  margin: 0,
                  fontSize: "1.5rem",
                  color: "#105D8E",
                  fontWeight: 600,
                }}
              >
                {t("cookies.settings", "Cookie Settings")}
              </h2>
              <button
                onClick={() => setShowSettings(false)}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: "4px",
                  display: "flex",
                  alignItems: "center",
                }}
                aria-label="Close cookie settings"
              >
                <X size={24} color="#666" />
              </button>
            </div>

            {/* Content */}
            <div style={{ padding: "24px" }}>
              <p
                style={{ color: "#666", marginBottom: "24px", lineHeight: 1.6 }}
              >
                {t(
                  "cookies.description",
                  "We use cookies to enhance your browsing experience, analyze site traffic, and personalize content. You can choose which cookies you want to allow. Your preferences will be saved for 12 months."
                )}
              </p>

              {/* Consent Version Info */}
              <p
                style={{
                  color: "#999",
                  fontSize: "0.75rem",
                  marginBottom: "16px",
                }}
              >
                {t("cookies.policyVersion", "Privacy Policy Version")}: {CONSENT_VERSION}
              </p>

              {/* Necessary Cookies */}
              <div
                style={{
                  padding: "16px",
                  background: "#f0f9ff",
                  borderRadius: "8px",
                  marginBottom: "16px",
                  border: "1px solid #bae6fd",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "8px",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <Shield size={20} color="#105D8E" />
                    <h3
                      style={{
                        margin: 0,
                        fontSize: "1rem",
                        fontWeight: 600,
                        color: "#333",
                      }}
                    >
                      {t("cookies.necessary.title", "Strictly Necessary")}
                    </h3>
                  </div>
                  <label
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      cursor: "not-allowed",
                      opacity: 0.6,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={true}
                      disabled
                      style={{
                        width: "20px",
                        height: "20px",
                        cursor: "not-allowed",
                      }}
                      aria-label="Necessary cookies - always enabled"
                    />
                    <span style={{ marginLeft: "8px", fontSize: "0.9rem", color: "#059669" }}>
                      {t("cookies.alwaysActive", "Always Active")}
                    </span>
                  </label>
                </div>
                <p style={{ margin: 0, fontSize: "0.9rem", color: "#666" }}>
                  {t(
                    "cookies.necessary.description",
                    "These cookies are essential for the website to function properly. They enable core functionality such as security, network management, and account access. You cannot disable these."
                  )}
                </p>
              </div>

              {/* Analytics Cookies */}
              <div
                style={{
                  padding: "16px",
                  background: "#f9fafb",
                  borderRadius: "8px",
                  marginBottom: "16px",
                  border: "1px solid #e5e7eb",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "8px",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <BarChart3 size={20} color="#6366f1" />
                    <h3
                      style={{
                        margin: 0,
                        fontSize: "1rem",
                        fontWeight: 600,
                        color: "#333",
                      }}
                    >
                      {t("cookies.analytics.title", "Analytics Cookies")}
                    </h3>
                  </div>
                  <label
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      cursor: "pointer",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={preferences.analytics}
                      onChange={(e) =>
                        setPreferences({
                          ...preferences,
                          analytics: e.target.checked,
                        })
                      }
                      style={{
                        width: "20px",
                        height: "20px",
                        cursor: "pointer",
                        accentColor: "#105D8E",
                      }}
                      aria-label="Enable analytics cookies"
                    />
                  </label>
                </div>
                <p style={{ margin: 0, fontSize: "0.9rem", color: "#666" }}>
                  {t(
                    "cookies.analytics.description",
                    "These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously. This helps us improve our services."
                  )}
                </p>
              </div>

              {/* Marketing Cookies */}
              <div
                style={{
                  padding: "16px",
                  background: "#f9fafb",
                  borderRadius: "8px",
                  marginBottom: "24px",
                  border: "1px solid #e5e7eb",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "8px",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <Megaphone size={20} color="#f59e0b" />
                    <h3
                      style={{
                        margin: 0,
                        fontSize: "1rem",
                        fontWeight: 600,
                        color: "#333",
                      }}
                    >
                      {t("cookies.marketing.title", "Marketing Cookies")}
                    </h3>
                  </div>
                  <label
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      cursor: "pointer",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={preferences.marketing}
                      onChange={(e) =>
                        setPreferences({
                          ...preferences,
                          marketing: e.target.checked,
                        })
                      }
                      style={{
                        width: "20px",
                        height: "20px",
                        cursor: "pointer",
                        accentColor: "#105D8E",
                      }}
                      aria-label="Enable marketing cookies"
                    />
                  </label>
                </div>
                <p style={{ margin: 0, fontSize: "0.9rem", color: "#666" }}>
                  {t(
                    "cookies.marketing.description",
                    "These cookies are used to deliver advertisements more relevant to you and your interests. They may also be used to limit the number of times you see an advertisement and measure the effectiveness of advertising campaigns."
                  )}
                </p>
              </div>

              {/* Privacy Policy Link */}
              <p style={{ fontSize: "0.85rem", color: "#666", marginBottom: "24px" }}>
                {t("cookies.learnMore", "Learn more about how we use cookies in our")}{" "}
                <a
                  href="/privacy-policy"
                  style={{ color: "#105D8E", textDecoration: "underline" }}
                  onClick={() => setShowSettings(false)}
                >
                  {t("cookies.privacyPolicy", "Privacy Policy")}
                </a>
                .
              </p>

              {/* Buttons */}
              <div
                style={{
                  display: "flex",
                  gap: "12px",
                  justifyContent: "flex-end",
                  flexWrap: "wrap",
                }}
              >
                <button
                  onClick={handleRejectAll}
                  style={{
                    padding: "10px 20px",
                    background: "#fff",
                    color: "#666",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    fontSize: "0.95rem",
                    fontWeight: 500,
                    cursor: "pointer",
                    fontFamily: "'Poppins', sans-serif",
                  }}
                >
                  {t("cookies.rejectAll", "Reject All")}
                </button>
                <button
                  onClick={handleSavePreferences}
                  style={{
                    padding: "10px 20px",
                    background: "#105D8E",
                    color: "#fff",
                    border: "none",
                    borderRadius: "6px",
                    fontSize: "0.95rem",
                    fontWeight: 500,
                    cursor: "pointer",
                    fontFamily: "'Poppins', sans-serif",
                  }}
                >
                  {t("cookies.savePreferences", "Save Preferences")}
                </button>
                <button
                  onClick={handleAcceptAll}
                  style={{
                    padding: "10px 20px",
                    background: "#059669",
                    color: "#fff",
                    border: "none",
                    borderRadius: "6px",
                    fontSize: "0.95rem",
                    fontWeight: 500,
                    cursor: "pointer",
                    fontFamily: "'Poppins', sans-serif",
                  }}
                >
                  {t("cookies.acceptAll", "Accept All")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cookie Banner */}
      {!showSettings && (
        <div
          style={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            background: "#1f2937",
            color: "#fff",
            padding: "16px 20px",
            boxShadow: "0 -4px 20px rgba(0, 0, 0, 0.15)",
            zIndex: 10000,
            fontFamily: "'Poppins', sans-serif",
          }}
          role="dialog"
          aria-modal="false"
          aria-label="Cookie consent banner"
        >
          <div
            style={{
              maxWidth: "1400px",
              margin: "0 auto",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: "16px",
            }}
          >
            <div style={{ flex: "1 1 400px", maxWidth: "700px" }}>
              <p
                style={{
                  margin: "0 0 4px 0",
                  fontSize: "1rem",
                  fontWeight: 600,
                }}
              >
                {t("cookies.bannerTitle", "We value your privacy")}
              </p>
              <p
                style={{
                  margin: 0,
                  fontSize: "0.9rem",
                  opacity: 0.9,
                  lineHeight: 1.5,
                }}
              >
                {inQuebec
                  ? t(
                      "cookies.bannerText",
                      "We use cookies to enhance your browsing experience, serve personalized content, and analyze our traffic. By clicking \"Accept All\", you consent to our use of cookies."
                    )
                  : t(
                      "cookies.bannerTextImplied",
                      "We use cookies to analyze site traffic and improve your experience. By continuing to browse, you accept this use. Marketing cookies remain off unless you opt in."
                    )}{" "}
                <a
                  href="/privacy-policy"
                  style={{ color: "#93c5fd", textDecoration: "underline" }}
                >
                  {t("cookies.learnMoreLink", "Learn more")}
                </a>
              </p>
            </div>

            <div
              style={{
                display: "flex",
                gap: "12px",
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <button
                onClick={() => setShowSettings(true)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "10px 16px",
                  background: "rgba(255, 255, 255, 0.1)",
                  color: "#fff",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                  borderRadius: "6px",
                  fontSize: "0.9rem",
                  fontWeight: 500,
                  cursor: "pointer",
                  fontFamily: "'Poppins', sans-serif",
                  transition: "background 0.2s",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = "rgba(255, 255, 255, 0.2)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = "rgba(255, 255, 255, 0.1)")
                }
              >
                <Settings size={16} />
                {inQuebec
                  ? t("cookies.settingsButton", "Customize")
                  : t("cookies.manageButton", "Manage cookies")}
              </button>

              {inQuebec && (
                <button
                  onClick={handleRejectAll}
                  style={{
                    padding: "10px 16px",
                    background: "transparent",
                    color: "#fff",
                    border: "1px solid rgba(255, 255, 255, 0.3)",
                    borderRadius: "6px",
                    fontSize: "0.9rem",
                    fontWeight: 500,
                    cursor: "pointer",
                    fontFamily: "'Poppins', sans-serif",
                    transition: "background 0.2s",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = "rgba(255, 255, 255, 0.1)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background = "transparent")
                  }
                >
                  {t("cookies.rejectAll", "Reject All")}
                </button>
              )}

              <button
                onClick={inQuebec ? handleAcceptAll : handleAcknowledge}
                style={{
                  padding: "10px 24px",
                  background: "#059669",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  fontSize: "0.9rem",
                  fontWeight: 600,
                  cursor: "pointer",
                  fontFamily: "'Poppins', sans-serif",
                  transition: "background 0.2s",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "#047857")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "#059669")}
              >
                {inQuebec
                  ? t("cookies.acceptAll", "Accept All")
                  : t("cookies.gotIt", "Got it")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default CookieBanner;
